import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '~/lib/api';

/**
 * The one place burgundy lives. Scoped via `data-ground="landing"` on the
 * <main> element so tokens.css flips the ground color and focus ring to
 * the wax family. The rest of the app never sees these.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Response carries { session, expiresAt } alongside Set-Cookie but we
      // ignore the body here: the cookie is what authenticates the next
      // request. The dashboard mounts, /dashboard/me rides the cookie, and
      // we're in.
      await api('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Something went wrong. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      data-ground="landing"
      className="grid min-h-full place-items-center bg-surface-canvas p-6"
    >
      <div className="w-full max-w-[400px]">
        <div className="mb-10 flex items-center gap-2.5">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-wax text-[14px] font-semibold text-accent-wax-ink shadow-[inset_0_1px_0_rgba(255,255,255,.1)]">
            S
          </span>
          <span className="font-serif text-[22px] -tracking-[0.01em]">Seven</span>
        </div>

        <h1 className="mb-2 font-serif text-[36px] font-normal leading-[40px] -tracking-[0.02em]">
          Sign <em className="italic text-accent-wax">in</em>
        </h1>
        <p className="mb-8 text-[15px] leading-[22px] text-ink-secondary">
          Dashboard for Seven card issuing.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field label="Email">
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-accent-wax focus:bg-surface-slip focus:shadow-[0_0_0_2px_var(--accent-wax-tint)] focus:outline-none"
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-9 w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-accent-wax focus:bg-surface-slip focus:shadow-[0_0_0_2px_var(--accent-wax-tint)] focus:outline-none"
            />
          </Field>

          {error && (
            <div className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] text-stop">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 inline-flex h-9 items-center justify-center gap-2 rounded-md bg-accent-wax px-3.5 text-[14.5px] font-semibold text-accent-wax-ink shadow-[inset_0_1px_0_rgba(255,255,255,.14),0_1px_2px_rgba(94,26,40,.2)] transition-colors hover:bg-accent-wax-deep disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="pt-2 text-center text-[14px] text-ink-secondary">
            New to Seven?{' '}
            <Link
              to="/signup"
              className="font-semibold text-accent-wax hover:text-accent-wax-deep"
            >
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13.5px] font-medium text-ink-primary">{label}</span>
      {children}
    </label>
  );
}
